import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { feedbackService } from '@/services/feedbackService'
import { useUserAuthStore } from '@/stores/userAuthStore'
import {
  FEEDBACK_STATUSES,
  migrateStatus,
  groupFeedbackByStatus
} from '@/utils/feedbackUtils'

export const useFeedbackStore = defineStore('feedback', () => {
  const feedbackList = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const filters = ref({
    status: ['backlog', 'in_progress', 'stuck'], // Hide complete by default
    type: [],
    priority: []
  })

  const filteredFeedback = computed(() => {
    let filtered = [...feedbackList.value]
    
    // Filter by status (multi-select)
    if (filters.value.status && filters.value.status.length > 0) {
      filtered = filtered.filter(f => filters.value.status.includes(f.status))
    }
    
    // Filter by type (multi-select)
    if (filters.value.type && filters.value.type.length > 0) {
      filtered = filtered.filter(f => filters.value.type.includes(f.type))
    }
    
    // Filter by priority (multi-select)
    if (filters.value.priority && filters.value.priority.length > 0) {
      filtered = filtered.filter(f => filters.value.priority.includes(f.priority))
    }
    
    return filtered.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
      const aPriority = priorityOrder[a.priority] || 3
      const bPriority = priorityOrder[b.priority] || 3
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }
      
      return b.timestamp?.toDate?.()?.getTime() - a.timestamp?.toDate?.()?.getTime() || 0
    })
  })

  const feedbackStats = computed(() => {
    const stats = {
      total: feedbackList.value.length,
      backlog: 0,
      inProgress: 0,
      stuck: 0,
      complete: 0,
      bugs: 0,
      features: 0,
      critical: 0,
      high: 0
    }

    feedbackList.value.forEach(feedback => {
      const status = migrateStatus(feedback.status)
      if (status === FEEDBACK_STATUSES.BACKLOG) stats.backlog++
      if (status === FEEDBACK_STATUSES.IN_PROGRESS) stats.inProgress++
      if (status === FEEDBACK_STATUSES.STUCK) stats.stuck++
      if (status === FEEDBACK_STATUSES.COMPLETE) stats.complete++

      // Only count non-complete items for tab counts
      const isComplete = status === FEEDBACK_STATUSES.COMPLETE
      if (feedback.type === 'bug' && !isComplete) stats.bugs++
      if (feedback.type === 'feature' && !isComplete) stats.features++

      if (feedback.priority === 'critical') stats.critical++
      if (feedback.priority === 'high') stats.high++
    })

    return stats
  })

  // Get bugs only
  const bugs = computed(() => {
    return feedbackList.value.filter(f => f.type === 'bug')
  })

  // Get features only
  const features = computed(() => {
    return feedbackList.value.filter(f => f.type === 'feature')
  })

  // Get bugs grouped by status for kanban
  const bugsByStatus = computed(() => {
    return groupFeedbackByStatus(bugs.value, 'bug')
  })

  // Get features grouped by status for kanban
  const featuresByStatus = computed(() => {
    return groupFeedbackByStatus(features.value, 'feature')
  })

  // Get unassigned items (no versionId) for backlog panel
  const unassignedItems = computed(() => {
    return feedbackList.value
      .filter(f => !f.versionId && migrateStatus(f.status) !== FEEDBACK_STATUSES.COMPLETE)
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
        return (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3)
      })
  })

  // Completed items, newest first — the backlog panel hides them by default,
  // so this backs its "Resolved" tab
  const resolvedItems = computed(() => {
    return feedbackList.value
      .filter(f => migrateStatus(f.status) === FEEDBACK_STATUSES.COMPLETE)
      .sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() ?? a.timestamp?.toDate?.()?.getTime() ?? 0
        const bTime = b.updatedAt?.toDate?.()?.getTime() ?? b.timestamp?.toDate?.()?.getTime() ?? 0
        return bTime - aTime
      })
  })

  const unassignedBugs = computed(() => {
    return unassignedItems.value.filter(f => f.type === 'bug')
  })

  const unassignedFeatures = computed(() => {
    return unassignedItems.value.filter(f => f.type === 'feature')
  })

  const submitFeedback = async (feedbackData) => {
    isLoading.value = true
    error.value = null
    
    try {
      const docId = await feedbackService.createFeedback(feedbackData)
      return docId
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const loadAllFeedback = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const feedback = await feedbackService.getAllFeedback()
      feedbackList.value = feedback
    } catch (err) {
      error.value = err.message
      console.error('Error loading feedback:', err)
    } finally {
      isLoading.value = false
    }
  }

  const updateFeedbackStatus = async (feedbackId, status) => {
    isLoading.value = true
    error.value = null
    
    try {
      // completedBy lets the onFeedbackCompleted trigger skip notifying
      // someone about completing their own submission
      const updates = { status }
      if (status === FEEDBACK_STATUSES.COMPLETE) {
        updates.completedBy = useUserAuthStore().user?.id || null
      }

      await feedbackService.updateFeedback(feedbackId, updates)

      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index !== -1) {
        feedbackList.value[index].status = status
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const addFeedbackNote = async (feedbackId, noteData) => {
    isLoading.value = true
    error.value = null
    
    try {
      // Get current feedback
      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index === -1) throw new Error('Feedback not found')
      
      const currentNotes = feedbackList.value[index].notes || []
      const updatedNotes = [...currentNotes, noteData]
      
      // Update in Firebase
      await feedbackService.updateFeedback(feedbackId, { notes: updatedNotes })
      
      // Update local state
      feedbackList.value[index].notes = updatedNotes
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteFeedback = async (feedbackId) => {
    isLoading.value = true
    error.value = null

    try {
      await feedbackService.deleteFeedback(feedbackId)
      feedbackList.value = feedbackList.value.filter(f => f.id !== feedbackId)
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateFeedbackPriority = async (feedbackId, priority) => {
    error.value = null

    try {
      await feedbackService.updateFeedback(feedbackId, { priority })

      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index !== -1) {
        feedbackList.value[index].priority = priority
      }
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const updateFeedbackType = async (feedbackId, type) => {
    error.value = null

    try {
      await feedbackService.updateFeedback(feedbackId, { type })

      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index !== -1) {
        feedbackList.value[index].type = type
      }
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const updateFeedbackOrder = async (feedbackId, order) => {
    error.value = null

    try {
      await feedbackService.updateFeedback(feedbackId, { order })

      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index !== -1) {
        feedbackList.value[index].order = order
      }
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const updateBulkOrder = async (items) => {
    error.value = null

    try {
      // Update each item's order in parallel
      await Promise.all(
        items.map((item, index) =>
          feedbackService.updateFeedback(item.id, { order: index })
        )
      )

      // Update local state
      items.forEach((item, index) => {
        const feedbackIndex = feedbackList.value.findIndex(f => f.id === item.id)
        if (feedbackIndex !== -1) {
          feedbackList.value[feedbackIndex].order = index
        }
      })
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const updateFeedbackField = async (feedbackId, updates) => {
    error.value = null
    try {
      await feedbackService.updateFeedback(feedbackId, updates)
      const index = feedbackList.value.findIndex(f => f.id === feedbackId)
      if (index !== -1) {
        Object.assign(feedbackList.value[index], updates)
      }
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const setFilter = (filterType, value) => {
    filters.value[filterType] = value
  }

  const resetFilters = () => {
    filters.value = {
      status: ['backlog', 'in_progress', 'stuck'], // Default: hide complete
      type: [],
      priority: []
    }
  }

  return {
    feedbackList,
    filteredFeedback,
    feedbackStats,
    bugs,
    features,
    bugsByStatus,
    featuresByStatus,
    unassignedItems,
    unassignedBugs,
    unassignedFeatures,
    resolvedItems,
    isLoading,
    error,
    filters,
    submitFeedback,
    loadAllFeedback,
    updateFeedbackStatus,
    updateFeedbackPriority,
    updateFeedbackType,
    updateFeedbackOrder,
    updateBulkOrder,
    addFeedbackNote,
    deleteFeedback,
    updateFeedbackField,
    setFilter,
    resetFilters
  }
})